import { Box, createStyles, Link, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import T from 'src/components/content-manager/translator/T';
import Section from 'src/components/elements/Section';
import BottomBar from 'src/components/page/BottomBar';
import { GROUP_ABOUT, GROUP_SERVICES, ROUTE_ABOUT, ROUTE_SERVICES, ROUTE_STORE } from 'src/config/routes';
import { AppRoute } from 'src/core/AppRoute';
import { useStoreRoutes } from 'src/core/hooks/store-routes';
import { useEnabledSiteModuleKeys } from 'src/core/provider/SiteProvider';
import Logo from 'src/resources/icons/Logo';

const useStyles = makeStyles(theme =>
    createStyles({
        box: {
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                textAlign: 'center',
            },
        },
        text: {
            fontSize: '1.1rem',
            lineHeight: '1.8rem',
        },
        textHeadlines: {
            fontWeight: 700,
            fontSize: '1.8rem',
            lineHeight: '2rem',
            marginBottom: theme.spacing(1.25),
        },
        logo: {
            width: 'auto',
            height: '100px',
        },
        routesList: {
            listStyle: 'none',
            marginTop: theme.spacing(5),
            padding: 0,
        },
        contactBlock: {
            [theme.breakpoints.down('md')]: { width: '100%' },
            [theme.breakpoints.down('sm')]: { order: 5 },
        },
    })
);

interface FooterProps {
    simpleFooter?: boolean;
}

const Footer: React.FC<FooterProps> = ({ simpleFooter }) => {
    const classes = useStyles();
    const enabledModules = useEnabledSiteModuleKeys();

    const { routes } = useStoreRoutes();

    return (
        <footer>
            {!simpleFooter && (
                <Section component="div" sx={{ bgcolor: 'grey.900', color: 'common.white' }} spacing="huge">
                    <Box className={clsx(classes.box, classes.text)}>
                        {/* Logo and Contact */}
                        <div className={classes.contactBlock}>
                            <Logo className={classes.logo} />
                            <Typography>
                                <T k="contact:company.value" />
                            </Typography>
                            <Typography>
                                <T k="contact:address.value" />
                            </Typography>
                            <Typography>
                                <T k="contact:phone.abbr" />: <T k="contact:phone.value" />
                            </Typography>
                            <Typography>
                                <T k="contact:email.abbr" />: <T k="contact:email.value" />
                            </Typography>
                        </div>
                        {/* Routes */}
                        {enabledModules.has('store') && <RouteList t_title={ROUTE_STORE.t_name} routes={routes} />}
                        {enabledModules.has('about') && <RouteList t_title={ROUTE_ABOUT.t_name} routes={GROUP_ABOUT} />}
                        {enabledModules.has('services') && (
                            <RouteList t_title={ROUTE_SERVICES.t_name} routes={GROUP_SERVICES} />
                        )}
                    </Box>
                </Section>
            )}
            <BottomBar />
        </footer>
    );
};

interface RouteListProps {
    t_title: string;
    routes: AppRoute[];
}

const RouteList: React.FC<RouteListProps> = ({ t_title, routes }) => {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <ul className={classes.routesList}>
            <li className={classes.textHeadlines}>{t(t_title)}</li>
            {routes.map(({ path, t_name, t_params }) => {
                return (
                    <li key={path}>
                        <Link component={RouterLink} to={path} color="inherit">
                            {t(t_name, t_params)}
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
};

export default Footer;
